<template>
    <RegularModal ref="modal" @closed="handleClosed">
        <template v-slot:title>
            {{ isEditMode ? translate("admin_mats.edit_mat") : translate("admin_mats.add_mat") }}
        </template>
        <template v-slot:body>
            <div class="field">
                <label class="label">{{ translate("admin_mats.mat_name") }}</label>
                <div class="control">
                    <input
                        v-model="matName"
                        type="text"
                        class="input"
                        :class="{ 'is-danger': getValidationError('name') }"
                        maxlength="45"
                        :placeholder="translate('admin_mats.mat_name')"
                        @keyup.enter="handleSubmit"
                    />
                </div>
                <p v-if="getValidationError('name')" class="help is-danger">
                    {{ getValidationError("name") }}
                </p>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("admin_mats.cancel") }}
                </button>
                <LoadingButton class="button is-primary" :is-loading="isSubmitting" @click="handleSubmit">
                    {{ translate("admin_mats.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { useModal } from "../Common/useModal";
    import { useMatsStore } from "./matsStore";
    import { Mat } from "../Common/types";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";

    const { modal, open, close } = useModal();
    const matsStore = useMatsStore();

    const matName = ref<string>("");
    const isSubmitting = ref<boolean>(false);
    const isEditMode = ref<boolean>(false);
    const editingMatId = ref<number | null>(null);

    function openForCreate() {
        isEditMode.value = false;
        editingMatId.value = null;
        matName.value = "";
        clearValidationErrors();
        open();
    }

    function openForEdit(mat: Mat) {
        isEditMode.value = true;
        editingMatId.value = mat.id;
        matName.value = mat.name;
        clearValidationErrors();
        open();
    }

    function handleSubmit() {
        clearValidationErrors();

        const trimmedName = matName.value.trim();

        if (!trimmedName) {
            matsStore.validationErrors = {
                name: [translate("admin_mat.name_required")],
            };
            return;
        }

        isSubmitting.value = true;

        const promise = isEditMode.value
            ? matsStore.updateMat({ matId: editingMatId.value!, name: trimmedName, eventId: matsStore.eventId })
            : matsStore.createMat({ name: trimmedName, eventId: matsStore.eventId });

        promise
            .then(() => {
                const message = isEditMode.value ? translate("admin_mats.mat_updated") : translate("admin_mats.mat_created");
                successToast(message);
                close();
            })
            .catch(error => {
                if (error.validationErrors) {
                    updateValidationErrors(error.validationErrors);
                } else {
                    handleError(error);
                }
            })
            .finally(() => {
                isSubmitting.value = false;
            });
    }

    function updateValidationErrors(validationErrorsToUpdate: Array<{ field: string; message: string[] }>) {
        const convertedToMap = validationErrorsToUpdate.reduce(
            (acc, item) => {
                acc[item.field] = item.message;
                return acc;
            },
            {} as Record<string, string[]>,
        );

        matsStore.validationErrors = convertedToMap;
    }

    function clearValidationErrors() {
        matsStore.clearValidationErrors();
    }

    function getValidationError(field: string): string {
        return matsStore.validationErrors[field] ? matsStore.validationErrors[field].join(" ") : "";
    }

    function handleClosed() {
        matName.value = "";
        clearValidationErrors();
        isEditMode.value = false;
        editingMatId.value = null;
    }

    defineExpose({ openForCreate, openForEdit });
</script>
